import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Receipt, Plus, FileText, Loader2, CheckCircle2, Archive, Trash2, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, TICKET_CATEGORIES } from '../lib/constants';

const INVOICE_HISTORY_STATUSES = ['paid'];

const INVOICE_STATUSES = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  approved: { label: 'Disetujui', color: 'bg-green-100 text-green-700' },
  paid: { label: 'Dibayar', color: 'bg-emerald-100 text-emerald-700' },
};

const InvoicesPage = () => {
  const { user, hasPermission } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ ticket_id: '', service_cost: 0, notes: '' });
  const [selectedTicketItems, setSelectedTicketItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ payment_note: '', payment_proof: null });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [listLocationFilter, setListLocationFilter] = useState('all');
  const [listAreaFilter, setListAreaFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Area is a wider grouping than lokasi (cabang): each location optionally belongs to an area.
  const branchLocations = locations.filter((l) => !l.is_warehouse);
  const areaOptions = Array.from(
    new Set(branchLocations.map((l) => l.area).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const locationAreaMap = locations.reduce((map, l) => {
    map[l.id] = l.area || null;
    return map;
  }, {});
  const listLocationsInArea = listAreaFilter === 'all'
    ? branchLocations
    : branchLocations.filter((l) => l.area === listAreaFilter);

  const handleListAreaFilterChange = (value) => {
    setListAreaFilter(value);
    if (value !== 'all' && listLocationFilter !== 'all' && locationAreaMap[listLocationFilter] !== value) {
      setListLocationFilter('all');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesRes, ticketsRes, locationsRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/tickets-simple'),
        api.get('/locations'),
      ]);
      setInvoices(invoicesRes.data);
      setTickets(ticketsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketSelect = async (ticketId) => {
    setForm({ ...form, ticket_id: ticketId, service_cost: 0 });
    if (!ticketId) {
      setSelectedTicketItems(null);
      return;
    }
    setLoadingItems(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}/items-summary`);
      setSelectedTicketItems(data);
    } catch (error) {
      toast.error('Gagal load barang tiket');
      setSelectedTicketItems(null);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post('/invoices/generate', {
        ticket_id: form.ticket_id,
        service_cost: parseFloat(form.service_cost) || 0,
        notes: form.notes,
      });
      toast.success('Invoice berhasil digenerate. Status tiket → Invoice Terbit');
      setShowDialog(false);
      setForm({ ticket_id: '', service_cost: 0, notes: '' });
      setSelectedTicketItems(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file max 2MB');
      return;
    }
    try {
      const base64 = await handleFileToBase64(file);
      setPaymentForm({ ...paymentForm, payment_proof: base64 });
      toast.success('Bukti bayar siap diupload');
    } catch (error) {
      toast.error('Gagal baca file');
    }
  };

  const handleMarkPaid = async () => {
    if (!payingInvoice) return;
    setProcessingPayment(true);
    try {
      await api.put(`/invoices/${payingInvoice.id}/mark-paid`, paymentForm);
      toast.success('Invoice ditandai sudah dibayar. Tiket → Selesai');
      setPayingInvoice(null);
      setPaymentForm({ payment_note: '', payment_proof: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal update pembayaran');
    } finally {
      setProcessingPayment(false);
    }
  };

  const canDeleteInvoice = (invoice) => {
    if (!user) return false;
    const isSuper = user.role === 'super_admin';
    if (invoice.type === 'transfer') return isSuper;
    if (invoice.status === 'paid') return isSuper;
    return hasPermission('mark_paid_invoice');
  };

  const handleDelete = async (invoice) => {
    const label = invoice.invoice_number || invoice.ticket_id;
    if (!window.confirm(`Yakin hapus invoice ${label}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/invoices/${invoice.id}`);
      toast.success(`Invoice ${label} berhasil dihapus`);
      setInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal hapus invoice');
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await api.get(`/exports/invoices?scope=${activeTab}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${activeTab}-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export berhasil diunduh');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal export');
    }
  };

  const handleExportExcelModal = async () => {
    try {
      const res = await api.get(`/exports/invoices/modal?scope=${activeTab}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-modal-${activeTab}-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export (Harga Modal) berhasil diunduh');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal export');
    }
  };

  const handlePrint = (invoice) => {
    const pi = invoice.payment_info || {};
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const absUrl = (u) => (!u ? '' : u.startsWith('http') || u.startsWith('data:') ? u : `${backend}${u}`);
    const isImg = (u) => u && (/\.(jpe?g|png|webp|gif)(\?|$)/i.test(u) || u.startsWith('data:image'));

    const paymentSection = pi.bank_name ? `
          <div class="payment-info">
            <h3 style="margin: 0 0 12px 0; color: #E05D3A;">Informasi Pembayaran</h3>
            <p style="margin: 4px 0;"><strong>Kirim ke:</strong> ${pi.location_name || ''} (${pi.location_id || ''})</p>
            <p style="margin: 4px 0;"><strong>Bank:</strong> ${pi.bank_name}</p>
            <p style="margin: 4px 0;"><strong>No. Rekening:</strong> <span style="font-family: monospace; font-size: 16px;">${pi.account_number || '-'}</span></p>
            <p style="margin: 4px 0;"><strong>Atas Nama:</strong> ${pi.account_holder || '-'}</p>
          </div>
    ` : '';

    const expenseSection = (invoice.expenses && invoice.expenses.length > 0) ? `
          <div class="details" style="margin-top: 24px;">
            <h3 style="margin: 0 0 8px 0; color: #E05D3A;">Rincian Pembelian Langsung</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
              <thead>
                <tr style="background:#F8F9FA;">
                  <th style="text-align:left; padding:8px; border:1px solid #E5E7EB;">Deskripsi</th>
                  <th style="text-align:left; padding:8px; border:1px solid #E5E7EB;">Vendor</th>
                  <th style="text-align:right; padding:8px; border:1px solid #E5E7EB;">Nominal</th>
                  <th style="text-align:left; padding:8px; border:1px solid #E5E7EB;">Bukti</th>
                </tr>
              </thead>
              <tbody>
                ${invoice.expenses.map((e) => `
                  <tr>
                    <td style="padding:8px; border:1px solid #E5E7EB;">${e.description || '-'}${e.note ? `<div style="color:#6C757D; font-size:11px; margin-top:2px;">${e.note}</div>` : ''}</td>
                    <td style="padding:8px; border:1px solid #E5E7EB;">${e.vendor || '-'}</td>
                    <td style="padding:8px; border:1px solid #E5E7EB; text-align:right; font-weight:600;">${formatCurrency(e.amount || 0)}</td>
                    <td style="padding:8px; border:1px solid #E5E7EB;">${
                      e.receipt_url
                        ? (isImg(e.receipt_url)
                            ? `<img src="${absUrl(e.receipt_url)}" style="max-width:300px; max-height:400px; border:1px solid #E5E7EB; border-radius:4px;" />`
                            : `<a href="${absUrl(e.receipt_url)}" target="_blank" style="color:#E05D3A;">Buka file</a>`)
                        : '-'
                    }</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
    ` : '';

    const proofSection = invoice.payment_proof ? `
          <div class="details" style="margin-top: 24px;">
            <h3 style="margin: 0 0 8px 0; color: #E05D3A;">Bukti Pembayaran</h3>
            ${isImg(invoice.payment_proof)
              ? `<img src="${absUrl(invoice.payment_proof)}" style="max-width:300px; max-height:400px; border:1px solid #E5E7EB; border-radius:4px;" />`
              : `<a href="${absUrl(invoice.payment_proof)}" target="_blank" style="color:#E05D3A;">Buka bukti pembayaran</a>`}
            ${invoice.payment_note ? `<p style="margin-top:8px; color:#6C757D; font-size:13px;">Catatan: ${invoice.payment_note}</p>` : ''}
          </div>
    ` : '';

    const prReceiptsSection = (invoice.pr_receipts && invoice.pr_receipts.length > 0) ? `
          <div class="details" style="margin-top: 24px;">
            <h3 style="margin: 0 0 8px 0; color: #E05D3A;">Bukti Tanda Terima Barang (Purchase Request)</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
              <thead>
                <tr style="background:#F8F9FA;">
                  <th style="text-align:left; padding:8px; border:1px solid #E5E7EB;">Barang</th>
                  <th style="text-align:right; padding:8px; border:1px solid #E5E7EB;">Qty × Harga</th>
                  <th style="text-align:left; padding:8px; border:1px solid #E5E7EB;">Tanda Terima</th>
                </tr>
              </thead>
              <tbody>
                ${invoice.pr_receipts.map((r) => `
                  <tr>
                    <td style="padding:8px; border:1px solid #E5E7EB;">${r.item_name || '-'}</td>
                    <td style="padding:8px; border:1px solid #E5E7EB; text-align:right;">${r.quantity} × ${formatCurrency(r.unit_price || 0)}</td>
                    <td style="padding:8px; border:1px solid #E5E7EB;">${
                      r.receipt_url
                        ? (isImg(r.receipt_url)
                            ? `<img src="${absUrl(r.receipt_url)}" style="max-width:300px; max-height:400px; border:1px solid #E5E7EB; border-radius:4px;" />`
                            : `<a href="${absUrl(r.receipt_url)}" target="_blank" style="color:#E05D3A;">Buka file</a>`)
                        : '-'
                    }</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
    ` : '';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number || invoice.ticket_id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #1A1D20; }
            .header { border-bottom: 3px solid #E05D3A; padding-bottom: 20px; margin-bottom: 30px; }
            .title { font-size: 32px; margin: 0; }
            .subtitle { color: #6C757D; margin-top: 5px; }
            .info-row { display: flex; justify-content: space-between; margin-bottom: 12px; padding: 12px; background: #F8F9FA; border-radius: 4px; }
            .total-row { display: flex; justify-content: space-between; margin-top: 20px; padding: 15px; background: #E05D3A; color: white; font-weight: bold; font-size: 18px; border-radius: 6px; }
            .details { margin: 20px 0; }
            .details p { margin: 8px 0; }
            .payment-info { margin-top: 30px; padding: 20px; background: #FFF4EF; border-left: 4px solid #E05D3A; border-radius: 4px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="title">INVOICE</h1>
            <p class="subtitle">HotelDesk - Sistem Ticketing Internal</p>
          </div>
          <div class="details">
            <p><strong>Nomor:</strong> ${invoice.invoice_number || invoice.ticket_id}</p>
            <p><strong>Tiket:</strong> ${invoice.ticket_id}</p>
            <p><strong>Deskripsi:</strong> ${invoice.ticket_title || '-'}</p>
            <p><strong>Tanggal:</strong> ${formatDate(invoice.created_at)}</p>
            <p><strong>Status:</strong> ${INVOICE_STATUSES[invoice.status]?.label || invoice.status}</p>
          </div>
          <div class="info-row">
            <span>Biaya Jasa</span>
            <strong>${formatCurrency(invoice.service_cost)}</strong>
          </div>
          <div class="info-row">
            <span>Biaya Material/Barang</span>
            <strong>${formatCurrency(invoice.material_cost)}</strong>
          </div>
          ${(invoice.direct_cost || 0) > 0 ? `
          <div class="info-row">
            <span>Biaya Pembelian Langsung</span>
            <strong>${formatCurrency(invoice.direct_cost)}</strong>
          </div>` : ''}
          <div class="total-row">
            <span>TOTAL</span>
            <span>${formatCurrency(invoice.total_cost)}</span>
          </div>
          ${expenseSection}
          ${prReceiptsSection}
          ${paymentSection}
          ${proofSection}
          ${invoice.notes ? `<div class="details" style="margin-top: 20px;"><p><strong>Catatan:</strong></p><p>${invoice.notes}</p></div>` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const totalPreview = (parseFloat(form.service_cost) || 0) + (selectedTicketItems?.total_material_cost || 0);
  const availableTickets = tickets.filter((t) => {
    if (t.is_invoiced) return false;
    if (locationFilter !== 'all' && t.location_id !== locationFilter) return false;
    return true;
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="invoices-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Invoice</p>
            <h1 className="text-4xl font-medium tracking-tight">Daftar Invoice</h1>
            <p className="text-muted-foreground mt-1">Invoice tiket & transfer stok internal</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportExcel}
              data-testid="export-invoices-button"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            {hasPermission('export_invoice_cost') && (
              <Button
                variant="outline"
                onClick={handleExportExcelModal}
                data-testid="export-invoices-modal-button"
                title="Export dengan basis Harga Modal (biaya internal), termasuk margin"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export Excel (Harga Modal)
              </Button>
            )}
            {hasPermission('generate_invoice') && (
              <Button
                onClick={() => setShowDialog(true)}
                className="bg-primary hover:bg-primary/90"
                data-testid="generate-invoice-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Generate Invoice
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList data-testid="invoice-tabs">
              <TabsTrigger value="active" data-testid="invoice-tab-active">
                <Receipt className="w-4 h-4 mr-2" />
                Belum Dibayar ({invoices.filter((i) => !INVOICE_HISTORY_STATUSES.includes(i.status)).length})
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="invoice-tab-history">
                <Archive className="w-4 h-4 mr-2" />
                Riwayat ({invoices.filter((i) => INVOICE_HISTORY_STATUSES.includes(i.status)).length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Card className="border-border">
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 border-b border-border bg-secondary/20">
            <div>
              <Label className="text-xs">Cari (nomor / tiket / judul)</Label>
              <Input
                placeholder="Ketik untuk mencari..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="invoice-search-input"
                className="h-9"
              />
            </div>
            {areaOptions.length > 0 && (
              <div>
                <Label className="text-xs">Area</Label>
                <Select value={listAreaFilter} onValueChange={handleListAreaFilterChange}>
                  <SelectTrigger className="h-9" data-testid="invoice-filter-area">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Area</SelectItem>
                    {areaOptions.map((area) => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Cabang</Label>
              <Select value={listLocationFilter} onValueChange={setListLocationFilter}>
                <SelectTrigger className="h-9" data-testid="invoice-filter-location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Cabang</SelectItem>
                  {listLocationsInArea.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dari Tanggal</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="invoice-filter-from" />
            </div>
            <div>
              <Label className="text-xs">Sampai</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="invoice-filter-to" />
            </div>
          </div>
          <CardContent className="p-0">
            {(() => {
              const q = searchQuery.trim().toLowerCase();
              const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
              const toTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
              const filteredInvoices = invoices.filter((i) => {
                if (activeTab === 'history' ? !INVOICE_HISTORY_STATUSES.includes(i.status) : INVOICE_HISTORY_STATUSES.includes(i.status)) return false;
                if (q) {
                  const hay = `${i.invoice_number || ''} ${i.ticket_id || ''} ${i.ticket_title || ''}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                if (listLocationFilter !== 'all') {
                  const loc = i.location_id || i.payment_info?.location_id;
                  if (loc !== listLocationFilter) return false;
                }
                if (fromTs || toTs) {
                  const ts = i.created_at ? new Date(i.created_at).getTime() : 0;
                  if (fromTs && ts < fromTs) return false;
                  if (toTs && ts > toTs) return false;
                }
                return true;
              });
              if (loading) {
                return (
                  <div className="p-12 text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                  </div>
                );
              }
              if (filteredInvoices.length === 0) {
                return (
                  <div className="p-12 text-center text-muted-foreground">
                    <Receipt className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>{activeTab === 'history' ? 'Belum ada invoice dibayar' : 'Belum ada invoice belum dibayar'}</p>
                  </div>
                );
              }
              return (
                <div className="divide-y divide-border">
                  {filteredInvoices.map((invoice, idx) => (
                  <div key={idx} className="p-5" data-testid={`invoice-row-${idx}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                          <span className="font-mono text-xs sm:text-sm">{invoice.invoice_number || invoice.ticket_id}</span>
                          {invoice.type === 'transfer' && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs">
                              Transfer Stok
                            </Badge>
                          )}
                          {invoice.ticket_id && !invoice.type && (
                            <Badge variant="outline" className="font-mono text-xs">
                              Tiket: {invoice.ticket_id}
                            </Badge>
                          )}
                          {invoice.status === 'paid' ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              ✓ Sudah Dibayar
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              Belum Dibayar
                            </Badge>
                          )}
                        </div>
                        <p className="font-medium mb-1">{invoice.ticket_title || 'Invoice'}</p>
                        <p className="text-xs text-muted-foreground mb-2">{formatDate(invoice.created_at)}</p>
                        <div className="flex gap-3 sm:gap-4 text-xs sm:text-sm flex-wrap">
                          <span className="text-muted-foreground">
                            Jasa: <span className="font-medium text-foreground">{formatCurrency(invoice.service_cost)}</span>
                          </span>
                          <span className="text-muted-foreground">
                            Material: <span className="font-medium text-foreground">{formatCurrency(invoice.material_cost)}</span>
                          </span>
                          {invoice.location_id && (
                            <span className="text-muted-foreground">
                              Untuk: <span className="font-medium text-foreground font-mono">{invoice.location_id}</span>
                            </span>
                          )}
                        </div>
                        {invoice.payment_info?.bank_name && (
                          <div className="mt-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                            <div className="flex items-center gap-2 mb-1">
                              <FileText className="w-3 h-3 text-primary" />
                              <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Bayar ke Rekening</p>
                            </div>
                            <p className="text-sm break-words">
                              <span className="font-medium">{invoice.payment_info.bank_name}</span>
                              <span className="mx-2 text-muted-foreground">•</span>
                              <span className="font-mono">{invoice.payment_info.account_number}</span>
                              <span className="mx-2 text-muted-foreground">•</span>
                              <span className="text-muted-foreground">a.n. {invoice.payment_info.account_holder}</span>
                            </p>
                          </div>
                        )}
                        {invoice.status === 'paid' && (
                          <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                            <p className="text-xs uppercase tracking-wider font-bold text-emerald-800 mb-1">Info Pembayaran</p>
                            {invoice.payment_note && (
                              <p className="text-sm text-emerald-900 mb-1">{invoice.payment_note}</p>
                            )}
                            <p className="text-xs text-emerald-700">
                              Dibayar: {formatDate(invoice.paid_at || invoice.payment_date)}
                            </p>
                            {invoice.payment_proof && (
                              <a
                                href={invoice.payment_proof.startsWith('http') || invoice.payment_proof.startsWith('data:') ? invoice.payment_proof : `${process.env.REACT_APP_BACKEND_URL}${invoice.payment_proof}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 mt-2 text-xs text-emerald-700 hover:underline"
                                data-testid={`view-proof-${idx}`}
                              >
                                <FileText className="w-3 h-3" /> Lihat Bukti Bayar
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-right w-full sm:w-auto">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total</p>
                        <p className="text-xl sm:text-2xl font-medium text-primary whitespace-nowrap">{formatCurrency(invoice.total_cost)}</p>
                        <div className="flex gap-2 mt-2 justify-end flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePrint(invoice)}
                            data-testid={`print-invoice-${idx}`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Print
                          </Button>
                          {invoice.status !== 'paid' && hasPermission('mark_paid_invoice') && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => setPayingInvoice(invoice)}
                              data-testid={`mark-paid-${idx}`}
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Tandai Dibayar
                            </Button>
                          )}
                          {canDeleteInvoice(invoice) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                              onClick={() => handleDelete(invoice)}
                              data-testid={`delete-invoice-${idx}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Hapus
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Generate Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Generate Invoice dari Tiket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Filter Lokasi</Label>
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger data-testid="invoice-location-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Lokasi</SelectItem>
                    {locations.filter((l) => !l.is_warehouse).map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Pilih Tiket *</Label>
                <Select value={form.ticket_id} onValueChange={handleTicketSelect}>
                  <SelectTrigger data-testid="invoice-ticket-select">
                    <SelectValue placeholder="Pilih tiket yang akan di-invoice" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTickets.length === 0 ? (
                      <SelectItem value="_none" disabled>Tidak ada tiket yang bisa di-invoice</SelectItem>
                    ) : (
                      availableTickets.map((t) => (
                        <SelectItem key={t.ticket_number} value={t.ticket_number}>
                          {t.ticket_number} — {t.title}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {availableTickets.length} tiket tersedia. Yang sudah di-invoice otomatis di-filter.
                </p>
              </div>

              {/* Selected ticket info */}
              {form.ticket_id && (() => {
                const t = tickets.find((x) => x.ticket_number === form.ticket_id);
                if (!t) return null;
                return (
                  <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                    <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Info Tiket</p>
                    <p className="text-sm font-medium">{t.title}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-xs">{TICKET_CATEGORIES[t.category] || t.category}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{t.location_id}</Badge>
                      <span className="text-xs text-muted-foreground">Estimasi awal: {formatCurrency(t.estimated_cost)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Items preview */}
              {loadingItems ? (
                <div className="flex items-center gap-2 p-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Memuat barang...</span>
                </div>
              ) : selectedTicketItems && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="p-3 bg-secondary/50 border-b border-border flex items-center justify-between">
                    <p className="text-sm font-semibold">Barang yang Dialokasi ke Tiket</p>
                    <Badge variant="outline">{selectedTicketItems.item_count} item</Badge>
                  </div>
                  {selectedTicketItems.items.length > 0 ? (
                    <>
                      <div className="divide-y divide-border">
                        {selectedTicketItems.items.map((item, idx) => (
                          <div key={idx} className="p-3 flex items-center justify-between text-sm">
                            <div className="flex-1">
                              <p className="font-medium">{item.item_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.quantity} {item.unit} × {formatCurrency(item.unit_price)}
                                {item.from_location !== '-' && (
                                  <span className="ml-2">• Dari <span className="font-mono">{item.from_location}</span></span>
                                )}
                              </p>
                            </div>
                            <span className="font-medium">{formatCurrency(item.subtotal)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="p-3 bg-primary/5 border-t border-primary/20 flex justify-between">
                        <span className="text-sm font-semibold">Subtotal Barang</span>
                        <span className="font-medium text-primary">{formatCurrency(selectedTicketItems.total_material_cost)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      Tidak ada barang dialokasi ke tiket ini.
                      <br />
                      <span className="text-xs">Alokasi barang dari halaman detail tiket dulu.</span>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Biaya Jasa / Tenaga Kerja (Rp) — input manual</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.service_cost}
                  onChange={(e) => setForm({ ...form, service_cost: e.target.value })}
                  data-testid="invoice-service-cost-input"
                />
                <p className="text-xs text-muted-foreground">
                  Contoh: biaya teknisi, ongkos vendor, jasa instalasi
                </p>
              </div>

              <div className="space-y-2">
                <Label>Catatan (opsional)</Label>
                <Textarea
                  placeholder="Catatan tambahan untuk invoice..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  data-testid="invoice-notes-input"
                />
              </div>

              {/* Total live preview */}
              {form.ticket_id && (
                <div className="p-4 rounded-lg bg-primary text-primary-foreground">
                  <div className="flex items-center justify-between mb-2 text-sm opacity-90">
                    <span>Biaya Jasa</span>
                    <span>{formatCurrency(parseFloat(form.service_cost) || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between mb-3 text-sm opacity-90">
                    <span>Biaya Material</span>
                    <span>{formatCurrency(selectedTicketItems?.total_material_cost || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-white/20">
                    <span className="font-semibold text-lg">Total Invoice</span>
                    <span className="font-medium text-2xl">{formatCurrency(totalPreview)}</span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Batal</Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || !form.ticket_id}
                className="bg-primary hover:bg-primary/90"
                data-testid="save-invoice-button"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generate...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Generate Invoice
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Paid Dialog */}
        <Dialog open={!!payingInvoice} onOpenChange={(v) => !v && setPayingInvoice(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Tandai Invoice Dibayar</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {payingInvoice && (
                <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                  <p className="text-xs text-muted-foreground">Invoice</p>
                  <p className="font-mono text-sm font-medium">{payingInvoice.invoice_number || payingInvoice.ticket_id}</p>
                  <p className="text-sm mt-1">{payingInvoice.ticket_title}</p>
                  <p className="text-lg font-medium text-primary mt-2">
                    {formatCurrency(payingInvoice.total_cost)}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Catatan Pembayaran</Label>
                <Textarea
                  placeholder="Contoh: Transfer via BCA 20 Juli 2026, ref XYZ123"
                  value={paymentForm.payment_note}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_note: e.target.value })}
                  rows={2}
                  data-testid="payment-note-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Upload Bukti Bayar (image, max 2MB)</Label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleProofUpload}
                  className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                  data-testid="payment-proof-input"
                />
                {paymentForm.payment_proof && (
                  <div className="mt-2 p-2 border border-border rounded-lg">
                    {paymentForm.payment_proof.startsWith('data:image') ? (
                      <img src={paymentForm.payment_proof} alt="Preview" className="max-h-40 rounded" />
                    ) : (
                      <p className="text-sm text-muted-foreground">File dokumen siap upload</p>
                    )}
                  </div>
                )}
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-xs text-emerald-900">
                  <strong>Info:</strong> Setelah ditandai dibayar, status tiket akan otomatis menjadi <strong>&quot;Selesai&quot;</strong>.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayingInvoice(null)}>Batal</Button>
              <Button
                onClick={handleMarkPaid}
                disabled={processingPayment}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="confirm-paid-button"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Konfirmasi Dibayar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default InvoicesPage;
